import { Box, Flex, Text } from "@mantine/core";
import React, { useMemo } from "react";
import type { GaugeZone } from "../utils/ReadingRiskUtils";

interface Props {
  zones: GaugeZone[];
  rawValue: number;
  height?: number;
}

const ZONE_COLORS: Record<string, string> = {
  green: "var(--mantine-color-green-5)",
  amber: "var(--mantine-color-yellow-5)",
  red: "var(--mantine-color-red-5)",
};

const MARKER_COLOR = "var(--mantine-color-dark-9)";

export const ThresholdGauge: React.FC<Props> = ({ zones, rawValue, height = 12 }) => {
  const totalRange = useMemo(() => {
    if (!zones.length) return 1;
    return zones[zones.length - 1].max - zones[0].min;
  }, [zones]);

  const markerPercent = useMemo(() => {
    if (!zones.length || totalRange <= 0) return 0;
    const globalMin = zones[0].min;
    const clamped = Math.max(globalMin, Math.min(rawValue, zones[zones.length - 1].max));
    return ((clamped - globalMin) / totalRange) * 100;
  }, [zones, rawValue, totalRange]);

  if (!zones.length) return null;

  const globalMin = zones[0].min;

  return (
    <Box>
      <Box pos="relative" style={{ height, borderRadius: height / 2, overflow: "hidden" }}>
        <Flex h="100%">
          {zones.map((zone, i) => {
            const widthPercent = ((zone.max - zone.min) / totalRange) * 100;
            return (
              <Box
                key={i}
                h="100%"
                style={{
                  width: `${widthPercent}%`,
                  backgroundColor: ZONE_COLORS[zone.color],
                  borderRadius:
                    i === 0
                      ? `${height / 2}px 0 0 ${height / 2}px`
                      : i === zones.length - 1
                        ? `0 ${height / 2}px ${height / 2}px 0`
                        : undefined,
                }}
              />
            );
          })}
        </Flex>

        <Box
          pos="absolute"
          top={-3}
          style={{
            left: `${markerPercent}%`,
            transform: "translateX(-50%)",
            width: 6,
            height: height + 6,
            borderRadius: 3,
            backgroundColor: MARKER_COLOR,
            border: "1.5px solid white",
            boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
          }}
        />
      </Box>

      <Flex justify="space-between" mt={4}>
        {zones.map((zone, i) => (
          <Text key={i} size="10px" c="dimmed" fw={600}>
            {i === 0 ? Math.round(globalMin) : Math.round(zone.min)}
          </Text>
        ))}
        <Text size="10px" c="dimmed" fw={600}>
          {Math.round(zones[zones.length - 1].max)}
        </Text>
      </Flex>
    </Box>
  );
};
